import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { faGift } from '@fortawesome/pro-regular-svg-icons';

import Price from '@v2/shared/components/Price';
import Spinner from '@v2/shared/components/Spinner';
import { CartItem, ProductType } from '@v2/shared/lib/types';

type Props = {
  item: CartItem;
  onRemove: (itemId: number) => Promise<void>;
  onToggleGift: (itemId: number, sendAsGift: boolean) => Promise<void>;
};

/**
 * One line of the drawer cart.
 *
 * A `div` rather than the `li` it used to be: the drawer owns the list item now, because
 * the gift offer is drawn inside the same item as the line it belongs to. Two `li`s
 * nested is invalid, and the offer has to be *inside* one to sit under its own line
 * rather than between two arbitrary ones.
 *
 * The gift checkbox sets a value rather than toggling one. `xCmd=toggle-gift`, which the
 * legacy cart uses, flips whatever is currently stored — so an impatient double click
 * lands back where it started and the customer is told nothing. `send_as_gift` on the
 * PATCH is idempotent.
 */
export default function CartLine({ item, onRemove, onToggleGift }: Props) {
  const [busy, setBusy] = useState<null | 'remove' | 'gift'>(null);

  const image = item.product.featured_image;
  const total = item.price - item.discount;

  // A postcard or an e-card is itself what gets sent to somebody, so there is no gift to
  // make of it — the legacy sale cart left the box off both.
  const giftable =
    item.product.product_type !== ProductType.TYPE_POSTCARD &&
    item.product.product_type !== ProductType.TYPE_ECARD;

  const run = async (kind: 'remove' | 'gift', action: () => Promise<void>) => {
    setBusy(kind);

    try {
      await action();
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex gap-3 py-4 border-b border-slate-100">
      {image?.url && (
        <img
          src={image.url}
          alt={image.alt_text || item.product.name}
          width={54}
          height={70}
          className="w-[54px] h-[70px] object-contain shrink-0"
          loading="lazy"
        />
      )}
      <div className="min-w-0 flex-grow">
        <p className="m-0 font-semibold text-sm text-slate-900 leading-snug">{item.product.name}</p>
        {item.sku.text && <p className="m-0 text-xs text-slate-500 mt-0.5">{item.sku.text}</p>}
        <div className="flex items-center justify-between mt-1.5 gap-2">
          <Price value={total} className="text-base" />
          <button
            type="button"
            className="text-slate-400 hover:text-red-600 disabled:opacity-50 p-1 -m-1"
            aria-label={`Remove ${item.product.name} from your cart`}
            disabled={busy !== null}
            onClick={() => run('remove', () => onRemove(item.id))}
          >
            {busy === 'remove' ? <Spinner /> : <FontAwesomeIcon icon={faTrashAlt} />}
          </button>
        </div>
        {giftable && (
          <label className="flex items-center gap-2 mt-2 text-xs font-medium text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              className="cursor-pointer"
              checked={item.send_as_gift}
              disabled={busy !== null}
              onChange={(event) => run('gift', () => onToggleGift(item.id, event.target.checked))}
            />
            <span className="inline-flex items-center gap-1">
              <FontAwesomeIcon icon={faGift} className="text-slate-400" />
              This is a gift
            </span>
            {busy === 'gift' && <Spinner className="text-slate-400" />}
          </label>
        )}
      </div>
    </div>
  );
}
